import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { C } from "@/theme";
import { useAuth } from "@/auth";
import { Button, Card, Field, Picker, FormScreen } from "@/ui";

const TIME_OPTIONS = (() => { const out = []; for (let h = 6; h <= 20; h++) for (const m of [0, 30]) { const t = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`; out.push({ key: t, label: t }); } return out; })();

export default function BookRun() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const riderId = user?.rider_id ?? "";
  const trainersQ = useQuery({ queryKey: ["trainers"], queryFn: () => api.get("/trainers") });
  const assignQ = useQuery({ queryKey: ["assignments"], queryFn: () => api.get("/assignments") });
  const pointsQ = useQuery({ queryKey: ["meeting-points"], queryFn: () => api.get("/meeting-points") });
  const myReqQ = useQuery({ queryKey: ["run-requests", riderId], queryFn: () => api.get(`/run-requests?rider_id=${riderId}`), enabled: !!riderId });
  const meQ = useQuery({ queryKey: ["rider", riderId], queryFn: () => api.get(`/riders/${riderId}`), enabled: !!riderId });
  const blockedStatus = meQ.data?.status === "Inactive" ? meQ.data.status : null;
  const activeTrainers = (trainersQ.data ?? []).filter((t) => t.active && t.role === "Tutor");
  const assignedTrainerId = (assignQ.data ?? []).find((a) => a.rider_id === riderId)?.trainer_id ?? "";
  const meetingPoints = pointsQ.data ?? [];
  const favouriteMeetingPoint = (() => {
    const counts = {};
    (myReqQ.data ?? []).forEach((r) => { if (r.meeting_point) counts[r.meeting_point] = (counts[r.meeting_point] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
  })();

  const [trainerId, setTrainerId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [meetingPoint, setMeetingPoint] = useState("");
  const [meetingPostcode, setMeetingPostcode] = useState("");
  const [note, setNote] = useState("");
  const [addingLocation, setAddingLocation] = useState(false);
  const [newLocName, setNewLocName] = useState("");
  const [newLocPostcode, setNewLocPostcode] = useState("");
  useEffect(() => { if (!trainerId && assignedTrainerId) setTrainerId(assignedTrainerId); }, [assignedTrainerId]);
  useEffect(() => { if (!meetingPoint && favouriteMeetingPoint) selectPoint(favouriteMeetingPoint); }, [favouriteMeetingPoint]);

  const selectPoint = (name) => { setMeetingPoint(name); setMeetingPostcode(meetingPoints.find((p) => p.name === name)?.postcode ?? ""); };
  const addLocation = useMutation({
    mutationFn: () => api.post("/meeting-points", { name: newLocName.trim(), postcode: newLocPostcode.trim() }),
    onSuccess: (created) => { qc.invalidateQueries({ queryKey: ["meeting-points"] }); setMeetingPoint(created.name); setMeetingPostcode(created.postcode ?? ""); setNewLocName(""); setNewLocPostcode(""); setAddingLocation(false); },
  });
  const save = useMutation({
    mutationFn: () => api.post("/run-requests", { rider_id: riderId, trainer_id: trainerId, date, time, meeting_point: meetingPoint, meeting_postcode: meetingPostcode, note }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["run-requests"] }); navigate(-1); },
  });

  return (
    <FormScreen title="Book a Training Run" saveLabel="Request" onClose={() => navigate(-1)} onSave={() => save.mutate()} saving={save.isPending} saveDisabled={!riderId || !trainerId || !date || !!blockedStatus}>
      {blockedStatus ? <Card testID="book-blocked-status"><p style={{ color: C.error }} className="text-[13px] font-semibold">Your account is {blockedStatus}. Please speak to the training team — new runs cannot be booked until it is set back to Active.</p></Card> : null}
      <Picker label="Tutor" testID="book-trainer-picker" placeholder="Select your tutor" value={trainerId} onSelect={setTrainerId} options={activeTrainers.map((t) => ({ key: t.id, label: t.name + (t.id === assignedTrainerId ? " (your tutor)" : ""), sub: "Tutor" }))} />
      <Field label="Date" value={date} onChange={setDate} type="date" testID="book-date" />
      <Picker label="Preferred time" testID="book-time-picker" placeholder="Select a time" value={time} onSelect={setTime} options={TIME_OPTIONS} />
      <Picker label="Meeting point" testID="book-meeting-picker" placeholder="Select a meeting point" value={meetingPoint} onSelect={selectPoint} options={meetingPoints.map((p) => ({ key: p.name, label: p.name, sub: p.postcode || undefined }))} />
      {meetingPoint && meetingPostcode ? <p style={{ color: C.muted }} className="text-xs font-semibold -mt-2">{meetingPostcode}</p> : null}
      {addingLocation ? (
        <Card>
          <p style={{ color: C.onSurface }} className="font-extrabold mb-2">Add a new meeting point</p>
          <div className="flex flex-col gap-3">
            <Field label="Name" value={newLocName} onChange={setNewLocName} placeholder="e.g. McDonalds Cannock" testID="book-newloc-name" />
            <Field label="Postcode" value={newLocPostcode} onChange={setNewLocPostcode} placeholder="e.g. WS11 8XP" testID="book-newloc-postcode" />
            <div className="flex gap-3"><Button label="Cancel" variant="secondary" onClick={() => setAddingLocation(false)} testID="book-newloc-cancel" /><Button label="Add" onClick={() => addLocation.mutate()} loading={addLocation.isPending} disabled={!newLocName.trim()} testID="book-newloc-save" /></div>
          </div>
        </Card>
      ) : (
        <button data-testid="book-add-location" onClick={() => setAddingLocation(true)} style={{ color: C.brandPrimary }} className="text-sm font-bold text-left">+ Add a new meeting point</button>
      )}
      <Field label="Note for your tutor (optional)" value={note} onChange={setNote} placeholder="Anything you'd like to focus on" multiline testID="book-note" />
      {!riderId ? <Card><p style={{ color: C.warning }} className="text-[13px] font-semibold">Your login isn't linked to a rider profile yet. Ask a club admin to link it.</p></Card> : null}
    </FormScreen>
  );
}
